import { coincideBusqueda, formatearMoneda, type ConfigItem, type OrigenConfig } from '@barbones/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSearch } from '@tanstack/react-router';
import { Lock, RotateCcw, Search, Settings2 } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { api, mensajeError } from '@/api/cliente';
import { useSesion } from '@/api/sesion';
import { EncabezadoPagina, ModuleLayout } from '@/components/layout/ModuleLayout';
import { SECCIONES_POR_MODULO } from '@/components/layout/secciones';
import { Button } from '@/components/ui/button';
import { Esqueleto, Insignia, Tarjeta, Vacio } from '@/components/ui/data';
import { Input, Select, Switch } from '@/components/ui/form';
import { cn } from '@/lib/utils';
import { avisarError } from '@/lib/avisos';
import { EditorOrigenes } from './EditorOrigenes';

/** Convierte el texto del editor al tipo que espera la configuración. */
function interpretar(item: ConfigItem, texto: string): unknown {
  switch (item.editor) {
    case 'entero':
      return Number.parseInt(texto, 10);
    case 'decimal':
      return Number.parseFloat(texto);
    case 'opciones':
      return typeof item.porDefecto === 'number' ? Number(texto) : texto;
    case 'lista_montos':
      return texto
        .split(',')
        .map((t) => t.trim())
        .filter(Boolean)
        .map((t) => Math.round(Number.parseFloat(t) * 100));
    default:
      return texto;
  }
}

function aTexto(item: ConfigItem, valor: unknown): string {
  if (item.editor === 'lista_montos' && Array.isArray(valor)) return valor.map((v) => (Number(v) / 100).toString()).join(', ');
  return String(valor ?? '');
}

function FilaConfig({ item, resaltada }: { item: ConfigItem; resaltada: boolean }) {
  const { puede } = useSesion();
  const queryClient = useQueryClient();
  const editable = puede(item.permisoEdicion);
  const [texto, setTexto] = useState(aTexto(item, item.valor));
  useEffect(() => setTexto(aTexto(item, item.valor)), [item]);

  const guardar = useMutation({
    mutationFn: (valor: unknown) => api<ConfigItem>(`/configuracion/${item.clave}`, { method: 'PUT', body: { valor } }),
    onSuccess: () => {
      toast.success(`“${item.titulo}” actualizado`);
      void queryClient.invalidateQueries({ queryKey: ['configuracion'] });
    },
    onError: (e) => avisarError(e),
  });

  const modificado = texto !== aTexto(item, item.valor);
  const esDefecto = JSON.stringify(item.valor) === JSON.stringify(item.porDefecto);

  const control = (() => {
    if (item.editor === 'origenes') {
      return <EditorOrigenes valor={item.valor as OrigenConfig[]} editable={editable} guardando={guardar.isPending} onGuardar={(lista) => guardar.mutate(lista)} />;
    }
    if (item.editor === 'booleano') {
      return (
        <Switch
          checked={Boolean(item.valor)}
          disabled={!editable || guardar.isPending}
          onCheckedChange={(v) => guardar.mutate(v)}
          aria-label={item.titulo}
        />
      );
    }
    if (item.editor === 'opciones') {
      return (
        <Select
          value={String(item.valor)}
          disabled={!editable || guardar.isPending}
          onChange={(e) => guardar.mutate(interpretar(item, e.target.value))}
          aria-label={item.titulo}
          className="w-full sm:w-72"
        >
          {item.opciones?.map((o) => (
            <option key={String(o.valor)} value={String(o.valor)}>
              {o.etiqueta}
            </option>
          ))}
        </Select>
      );
    }
    return (
      <form
        className="flex w-full items-center gap-2 sm:w-auto"
        onSubmit={(e) => {
          e.preventDefault();
          guardar.mutate(interpretar(item, texto));
        }}
      >
        <div className="relative w-full sm:w-56">
          <Input
            value={texto}
            onChange={(e) => setTexto(e.target.value)}
            disabled={!editable}
            type={item.editor === 'hora' ? 'time' : item.editor === 'entero' || item.editor === 'decimal' ? 'number' : 'text'}
            step={item.editor === 'decimal' ? '0.0001' : undefined}
            inputMode={item.editor === 'entero' ? 'numeric' : item.editor === 'decimal' ? 'decimal' : undefined}
            aria-label={item.titulo}
            className={cn('cifras', item.unidad && 'pr-20')}
          />
          {item.unidad && (
            <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-[12px] text-foreground-muted">
              {item.unidad}
            </span>
          )}
        </div>
        {modificado && (
          <Button type="submit" tamano="sm" cargando={guardar.isPending}>
            Guardar
          </Button>
        )}
      </form>
    );
  })();

  return (
    <div
      id={`cfg-${item.clave}`}
      className={cn(
        'flex flex-col gap-3 border-b border-border/70 px-4 py-4 transition-colors last:border-0',
        item.editor !== 'origenes' && 'sm:flex-row sm:items-center sm:justify-between',
        resaltada && 'bg-accent-soft/60',
      )}
    >
      <div className="min-w-0 max-w-xl">
        <p className="flex items-center gap-2 text-[14px] font-medium">
          {item.titulo}
          {!editable && <Lock className="size-3.5 text-foreground-muted" aria-label="Solo lectura" />}
          {!esDefecto && <Insignia tono="acento">Personalizado</Insignia>}
        </p>
        <p className="mt-0.5 text-[12.5px] text-foreground-muted">{item.descripcion}</p>
        {item.editor === 'lista_montos' && Array.isArray(item.valor) && (
          <p className="cifras mt-1 text-[12px] text-foreground-muted">
            {item.valor.map((v) => formatearMoneda(Number(v))).join(' · ')}
          </p>
        )}
      </div>
      <div className="flex items-center gap-2">
        {control}
        {/* Restaurar la lista de orígenes borraría opciones: la API lo rechaza. */}
        {editable && !esDefecto && item.editor !== 'booleano' && item.editor !== 'origenes' && (
          <Button
            variante="fantasma"
            tamano="icono"
            onClick={() => guardar.mutate(item.porDefecto)}
            aria-label={`Restablecer ${item.titulo} al valor por defecto`}
            title="Restablecer valor por defecto"
          >
            <RotateCcw />
          </Button>
        )}
      </div>
    </div>
  );
}

export function ConfiguracionPage() {
  const { clave } = useSearch({ strict: false }) as { clave?: string };
  const [busqueda, setBusqueda] = useState('');
  const consulta = useQuery({ queryKey: ['configuracion'], queryFn: () => api<ConfigItem[]>('/configuracion') });

  const porCategoria = useMemo(() => {
    const items = (consulta.data ?? []).filter((c) =>
      coincideBusqueda(`${c.titulo} ${c.descripcion} ${c.categoria} ${c.clave}`, busqueda),
    );
    const mapa = new Map<string, ConfigItem[]>();
    for (const item of items) mapa.set(item.categoria, [...(mapa.get(item.categoria) ?? []), item]);
    return [...mapa.entries()];
  }, [consulta.data, busqueda]);

  useEffect(() => {
    if (clave && consulta.data) document.getElementById(`cfg-${clave}`)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, [clave, consulta.data]);

  return (
    <ModuleLayout titulo="Configuración" secciones={SECCIONES_POR_MODULO.configuracion}>
      <EncabezadoPagina
        titulo="Configuración general"
        descripcion="Todo lo configurable del sistema. Los cambios aplican de inmediato y quedan en la bitácora."
      />
      <div className="relative mb-5 max-w-md">
        <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-foreground-muted" aria-hidden />
        <Input
          value={busqueda}
          onChange={(e) => setBusqueda(e.target.value)}
          placeholder="Buscar configuración (ej. tolerancia, dólar, propinas)…"
          className="h-10 pl-9"
          aria-label="Buscar configuración"
        />
      </div>

      {consulta.isLoading ? (
        <div className="flex flex-col gap-3">
          {Array.from({ length: 4 }, (_, i) => (
            <Esqueleto key={i} className="h-20" />
          ))}
        </div>
      ) : porCategoria.length === 0 ? (
        <Tarjeta>
          <Vacio icono={<Settings2 />} titulo="Sin coincidencias" descripcion={`No hay configuraciones que coincidan con “${busqueda}”.`} />
        </Tarjeta>
      ) : (
        <div className="flex flex-col gap-6">
          {porCategoria.map(([categoria, items]) => (
            <section key={categoria} aria-labelledby={`cat-${categoria}`}>
              <h2 id={`cat-${categoria}`} className="mb-2 text-[19px]">
                {categoria}
              </h2>
              <Tarjeta>
                {items.map((item) => (
                  <FilaConfig key={item.clave} item={item} resaltada={item.clave === clave} />
                ))}
              </Tarjeta>
            </section>
          ))}
        </div>
      )}
    </ModuleLayout>
  );
}
